import { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import DataGrid from '@components/common/DataGrid'
import ControlCheckbox, { TOGGLE_ROW } from '@components/common/ControlCheckbox'
import { ColumnFilterMatchModeContext } from '@components/common/columnFilterMatchModeContext'
import usePreferencesStore from '@store/preferencesStore'
import { useAliasedGridColumns } from './aliasedGridColumns'
import { useReportStripActions } from './reportRunStateContext'
import { trackGridTransform } from '@/analytics/gridTransforms'
// The four view options' factory defaults, shared with ReportGroupedGrid so the
// reset and the initialisers cannot drift — see that module's note.
import { GRID_DISPLAY_DEFAULTS, PAGE_EXTRAS_NOT_RESET_NOTE, THOUSANDS_PAGE_FLAG } from './gridDisplayDefaults'
import {
  numericSortValue,
  toNumber,
  formatLikeSample,
  summableColumns,
  columnDefinition,
  columnDataKinds,
  totaledColumnIds,
  columnSamples,
  metadataDefaultSort,
  columnAggregate,
  isColumnFilterable,
  isNumericColumn,
  footerMarksNumeric,
  currencyEligible,
  currencyMarkedColumnIds,
  currencyToggleActs,
  numericTotalColumnIds,
  showDollarNumeric,
  declaredAmountColumnIds,
  stripCurrencySymbol,
  displayHasCurrency,
  isHideableZeroRow,
  findStatusColumn,
  isInactiveStatusValue,
  isNameColumn,
  distinctDisplayValues,
  matchColumnFilter,
  detectNumberFormat,
  withCurrencySymbol,
  groupedDisplay,
  groupsThousands,
  thousandsToggleActs,
  formatCount,
  findAgingColumn,
  isStaleValue,
  parseCellDate,
  AGING_PRESET_DAYS,
  compareCellValues,
} from './reportGridUtils'
import { exportCell, exportText } from './exportCell'

/**
 * Reporting V2 — universal controls for FLAT (non-grouped) CB reports.
 *
 * Routes a flat CB Report through the shared DataGrid so every standard report
 * gets click-to-sort, column show/hide, drag-reorder, and persisted layout
 * (via preferencesStore, keyed by storageKey) — with zero backend changes.
 *
 * Grouped reports (group headers / footers) stay on <ReportGroupedGrid>, which
 * ReportView selects. This component assumes flat data.
 *
 * CB Report shape (deep-camelized):
 *   columns: [{ columnId, label, visible, isBlankColumn, columnAlignment, dataType }]
 *   reportData: [{ isGroup: false, dataCells: [{ columnId, displayValue, dataValue }] }]
 *   reportFooterCells: [{ columnId, displayValue }]
 */

// CB metadata alignment → 'left' | 'center' | 'right'.
const metaAlign = (col) => {
  switch (col?.columnAlignment) {
    case 'RIGHT': return 'right'
    case 'CENTER': return 'center'
    default: return 'left'
  }
}
const ALIGN_TEXT = { left: 'text-left', center: 'text-center', right: 'text-right' }
const EMPTY_ALIGN = {}

// CC-49732: `initialPageState` restores a saved view's page state on mount
// ({ columnFilters, toggles }); `pageStateRef` publishes a getter for the
// current page state so the Views menu can capture it (exportViewRef pattern).
// `showZeroToggle` (CC-49906): pages whose report definition carries its OWN
// zero-balance filter (e.g. Trial Balance "Show zero balances") pass false so
// the generic grid toggle doesn't duplicate it.
// `gridNote` (spec 092) is the mock's muted line beside the row count — a report
// explaining its own numbers where they are read ("Negative amounts are credit
// balances."). Optional; omitted, the strip shows the count alone.
const ReportDataGrid = ({ report, storageKey, exportViewRef, rowNoun, onFilteredChange, pageStateRef, initialPageState, showZeroToggle = true, showInactiveToggle = true, pageDisplayExtras, onResetPageDisplayExtras, gridNote, headerRow }) => {
  const prefsStore = usePreferencesStore()
  // Host-published strip buttons (Rename columns). Null on any page with no
  // provider above it, which is every non-report caller.
  const stripActions = useReportStripActions()
  const initialToggles = initialPageState?.toggles || null
  // CC-49341 / CC-49743: per-report currency-symbol toggle — TRI-STATE.
  // null = auto (server display untouched; the checkbox mirrors whether the
  // data already shows $), true = force $ onto amounts, false = strip it.
  // Persists like layout prefs; applies to cells and exports.
  const [forceCurrency, setForceCurrency] = useState(
    () => initialToggles?.forceCurrency ?? (storageKey ? prefsStore.getCurrencySymbol?.(storageKey) : null) ?? GRID_DISPLAY_DEFAULTS.forceCurrency
  )
  useEffect(() => {
    // Only an explicit user choice persists — auto stays auto.
    if (storageKey && forceCurrency !== null) prefsStore.setCurrencySymbol?.(storageKey, forceCurrency)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceCurrency, storageKey])
  // Spec 212: "Show commas in numbers", per report. ON (spec 211's separators) is
  // the default; only the user's own click is stored (THOUSANDS_PAGE_FLAG), so a
  // saved view's seed never overwrites their per-report choice.
  const [showThousands, setShowThousands] = useState(
    () => initialToggles?.showThousands ?? (storageKey ? prefsStore.getPageFlag?.(storageKey, THOUSANDS_PAGE_FLAG) : null) ?? GRID_DISPLAY_DEFAULTS.showThousands
  )

  // CC-49735: per-column alignment override (user choice, persisted per report)
  // layered over the report's own metadata alignment. Store getter returns null
  // when nothing is stored, so this memo dep stays stable.
  const alignOverrides = (storageKey && prefsStore.getColumnAlignment?.(storageKey)) || EMPTY_ALIGN
  const handleAlignmentChange = useCallback(
    (colId, align) => {
      // DataGrid's own <select> already emits `align`; this is the persistence
      // half. Not emitted twice — see gridTransforms for why the store is not
      // the seam.
      if (storageKey) prefsStore.setColumnAlignment?.(storageKey, colId, align)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storageKey]
  )

  // CC-50291: visible columns, with the firm's own name applied to any column
  // that opted in by carrying `aliasKey`, and that set published up to
  // `SpecializedReportShell` so an admin can rename them. Inert on the generic
  // report path and inside CsvReportGrid / AutoReportGrid, whose columns carry
  // no `aliasKey` — see `aliasedGridColumns.js` for why that opt-in exists.
  const cbColumns = useAliasedGridColumns(report)

  // CC-49944: what each column's rows actually hold (numeric / text / empty), so
  // the header tooltip states a type the data supports instead of guessing from
  // alignment alone.
  const dataKinds = useMemo(() => columnDataKinds(report), [report])
  // CC-49944: which columns the report actually totals, so a tooltip never
  // promises a total the report does not render (see columnDefinition).
  const totaledIds = useMemo(() => totaledColumnIds(report), [report])
  // CC-49944 (QA test 7): a formatted example value per column for the tooltip.
  const samples = useMemo(() => columnSamples(report, dataKinds), [report, dataKinds])
  // CC-53905: which columns' own source already marks them as currency — the
  // only Show $ signal an adapter column (no CB CellsFormat) has.
  const currencyMarked = useMemo(() => currencyMarkedColumnIds(report), [report])
  // CC-53905 review fix 4: the columns CB totals with a number. With the metadata
  // rule they make `showDollarNumeric`, the ONE numeric test every Show $ site
  // here uses (cells, totals, the toggle, the export view) — so a LEFT / CENTER
  // C column CB totals takes the "$" on screen exactly where the files put it.
  const numericTotals = useMemo(() => numericTotalColumnIds(report), [report])

  // react-table column defs. accessorFn returns the sortable value (dataValue
  // when present so numbers/dates sort correctly); cell renders CB's HTML displayValue.
  const columns = useMemo(
    () =>
      cbColumns.map((col) => {
        const id = String(col.columnId)
        // User override wins; falls back to CB's metadata alignment (CC-49735).
        const align = alignOverrides[id] || metaAlign(col)
        return {
          id,
          header: col.label,
          // Column metadata (CC-49302): definition tooltip + type-aware filtering.
          // `align` drives the cell, header, and footer alignment in DataGrid.
          //
          // D3 (2026-08-27): the definition is still COMPUTED and still travels in
          // meta, but whether it earns a header icon is decided at the gate, by
          // `isAuthoredDefinition` (./columnDefinitionGate) — today every string
          // `columnDefinition` returns is auto-generated filler, so no icon renders.
          // Do not "fix" that here by not computing it: the string is what a real
          // authored definition will replace, and the same predicate gates the
          // grouped grid, so the decision must stay in one place.
          meta: {
            definition: columnDefinition(col, {
              dataKind: dataKinds.get(id),
              hasTotal: totaledIds.has(id),
              sample: samples.get(id),
            }),
            align,
          },
          enableColumnFilter: isColumnFilterable(col),
          // First click sorts ascending for every column type (CC-49378).
          sortDescFirst: false,
          accessorFn: (row) => numericSortValue(row?.[id]?.sortVal, row?.[id]?.display ?? ''),
          // CC-49753: pin the shared comparator — TanStack's auto sortingFn
          // ordered letter-leading text before digit-leading text, the
          // opposite of V1's 0-9 → A-Z default.
          sortingFn: (rowA, rowB, columnId) =>
            compareCellValues(rowA.getValue(columnId), rowB.getValue(columnId)),
          // Forgiving per-column filter against the formatted value (CC-49379),
          // now via matchColumnFilter so it also honors the CC-49317 value picker
          // (filter value may be a string or { text, picked }) and spec 171's
          // operator (`{ text, mode }` / `{ text, picked, mode }`). The rows this
          // passes are the rows DataGrid publishes back through
          // onVisibleRowsChange, i.e. the totals recompute and the export view.
          filterFn: (row, _columnId, value) => matchColumnFilter(row.original?.[id]?.display, value),
          cell: ({ row }) => {
            // Spec 211: thousands separators first, so a "$" lands on "1,234.00";
            // spec 212: stripped instead while "Show commas in numbers" is off.
            const disp = groupedDisplay(col, row.original?.[id]?.display ?? '', numericTotals, showThousands)
            // CC-49341/CC-49743: explicit ON adds $ to amounts, explicit OFF
            // strips it; auto (null) leaves the server display untouched.
            // CC-53905: ON puts a "$" only on a column declared currency
            // (CB's CellsFormat 'C', or an adapter column its source marks),
            // and only onto the digits CB displayed — never re-rounded. Which
            // columns are numeric for it is `showDollarNumeric` (review fix 4).
            const html = !showDollarNumeric(col, numericTotals)
              ? disp
              : forceCurrency === true
                ? (currencyEligible(col, currencyMarked) ? withCurrencySymbol(disp) : disp)
                : forceCurrency === false
                  ? stripCurrencySymbol(disp)
                  : disp
            return (
              <span
                className={`block ${ALIGN_TEXT[align]}`}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: html }}
              />
            )
          },
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cbColumns, forceCurrency, showThousands, alignOverrides, dataKinds, totaledIds, samples, currencyMarked, numericTotals]
  )

  const data = useMemo(
    () =>
      (report?.reportData || [])
        .filter((r) => !r.isGroup)
        .map((r) => {
          const o = {}
          for (const cell of r.dataCells || []) {
            o[String(cell.columnId)] = { display: cell.displayValue, sortVal: cell.dataValue }
          }
          return o
        }),
    [report]
  )

  // CC-49317: distinct values per name-type column (client / matter / timekeeper
  // / attorney / user) feed the per-column multi-select value picker.
  const pickerValuesByCol = useMemo(() => {
    const out = {}
    for (const col of cbColumns) {
      if (isNameColumn(col)) {
        const id = String(col.columnId)
        out[id] = distinctDisplayValues(data.map((r) => r?.[id]?.display ?? ''))
      }
    }
    return out
  }, [cbColumns, data])

  // Inject picker values into the base column defs (kept separate so the base defs
  // don't need to depend on data).
  const columnsWithPickers = useMemo(
    () => columns.map((c) => {
      const pv = pickerValuesByCol[c.id]
      return pv && pv.length > 0 ? { ...c, meta: { ...c.meta, pickerValues: pv } } : c
    }),
    [columns, pickerValuesByCol]
  )

  // Row-exclusion toggles (CC-49318 $0 rows, CC-49319 inactive/closed). The
  // inactive toggle is only offered when the report carries a status-like column.
  const numericColIds = useMemo(
    () => cbColumns.filter(isNumericColumn).map((c) => String(c.columnId)),
    [cbColumns]
  )
  // CC-53905: Show $ is offered only where it can change something — some
  // numeric column takes a "$" under ON or shows one OFF would strip. Elsewhere
  // (every column CB declares N / F / '') the toggle is left out AND no explicit
  // choice reaches the export, so a stored ON cannot silently move CSV / Excel
  // off the server export (`currencyOverride` below). Review fix 4: over the same
  // numeric test the cells and the export view use (`showDollarNumeric`), so the
  // row is offered wherever a saved choice acts.
  const currencyActs = useMemo(() => currencyToggleActs(cbColumns, currencyMarked, numericTotals), [cbColumns, currencyMarked, numericTotals])
  // Spec 212: offered only where some figure is 1,000+, as Show $ is only where it acts.
  const thousandsActs = useMemo(() => thousandsToggleActs(report, cbColumns, numericTotals), [report, cbColumns, numericTotals])
  // The columns the Show $ choice acts on — `showDollarNumeric`; the auto state
  // below reads the same ones, so the checkbox mirrors what those columns show.
  const showDollarColIds = useMemo(
    () => cbColumns.filter((c) => showDollarNumeric(c, numericTotals)).map((c) => String(c.columnId)),
    [cbColumns, numericTotals]
  )
  // Spec 133: columns CB declares DECIMAL / INTEGER. Hide $0 never hides a row
  // holding a non-zero value in one of them, even where CB left-aligned it.
  const amountColIds = useMemo(() => declaredAmountColumnIds(cbColumns), [cbColumns])
  const statusCol = useMemo(() => findStatusColumn(cbColumns), [cbColumns])
  const statusColId = statusCol ? String(statusCol.columnId) : null
  // CC-49743: in auto mode the Show-$ checkbox mirrors what the data actually
  // shows, so unchecking it on a $-formatted report (e.g. P&L) strips the $.
  const serverShowsCurrency = useMemo(
    () => showDollarColIds.some((id) => data.some((r) => displayHasCurrency(r?.[id]?.display))),
    [showDollarColIds, data]
  )
  const effectiveCurrency = forceCurrency ?? serverShowsCurrency
  // Only offer the inactive/closed toggle when the status column ACTUALLY holds
  // inactive/closed values (bug #4) — otherwise a "Budget Status"/"Fee Status"/
  // task "Status" column would surface the toggle where it can do nothing.
  const hasInactiveRows = useMemo(
    () => !!statusColId && data.some((r) => isInactiveStatusValue(r?.[statusColId]?.display ?? '')),
    [statusColId, data]
  )
  const [hideZero, setHideZero] = useState(!!(initialToggles?.hideZero ?? GRID_DISPLAY_DEFAULTS.hideZero))
  const [hideInactive, setHideInactive] = useState(!!(initialToggles?.hideInactive ?? GRID_DISPLAY_DEFAULTS.hideInactive))

  // Aging / inactivity window (CC-49327): "show only rows not billed/active in
  // N days", keyed to a last-activity date column. Offered only when the column
  // exists AND its data actually parses as dates (bug-#4 lesson — a label match
  // alone could catch a currency column the control can't act on).
  // CC-49329: CB's column metadata declares a default sort (sortDirectionName
  // ASC/DESC via cb-api's enrichColumn) — honor it when the user hasn't sorted.
  // CC-49948: via the shared helper, which applies V1's narrower rule
  // (non-groupable columns only, ordered by SortOrder).
  const defaultSorting = useMemo(() => metadataDefaultSort(cbColumns), [cbColumns])

  const agingCol = useMemo(() => findAgingColumn(cbColumns), [cbColumns])
  const agingColId = agingCol ? String(agingCol.columnId) : null
  const hasAgingDates = useMemo(
    () => !!agingColId && data.some((r) => parseCellDate(r?.[agingColId]?.display ?? '') != null),
    [agingColId, data]
  )
  // agingMode: '' = off; '30'/'60'/'90' = preset; 'custom' = user-entered days.
  const [agingMode, setAgingMode] = useState(initialToggles?.agingMode || GRID_DISPLAY_DEFAULTS.agingMode)
  const [customDays, setCustomDays] = useState(initialToggles?.customDays || GRID_DISPLAY_DEFAULTS.customDays)
  const staleDays = useMemo(() => {
    if (agingMode === 'custom') {
      const n = parseInt(customDays, 10)
      return Number.isFinite(n) && n > 0 ? n : null
    }
    return agingMode ? parseInt(agingMode, 10) : null
  }, [agingMode, customDays])
  const agingActive = hasAgingDates && staleDays != null

  /*
   * E4 (2026-08-27) — "Reset grid layout" resets the View options too.
   *
   * Handed DOWN to DataGrid, which owns the menu row but must not own this
   * state: it is the generic list grid for nine non-report pages as well, and
   * `hideZero` / the aging window are report concepts. So the state's owner
   * supplies the reset and DataGrid.handleResetLayout calls it (see the
   * `onResetDisplayOptions` prop note there).
   *
   * Why it is a correctness fix and not tidying: these three toggles and the
   * aging window REMOVE ROWS. A user who ticked "Hide $0 / zero-balance rows"
   * and then clicked the reset two rows below it kept hiding rows on a WIP or
   * A/R grid while believing the grid was back to default — and the totals strip
   * recomputes over the visible rows (CC-49456), so the money moved with them.
   *
   * `null` for the currency toggle, not false: it is TRI-state (null = auto /
   * whatever the server sent = the default; false = actively strip the `$`,
   * which is an override, not a reset). DataGrid has already called
   * `resetPagePreferences`, which deletes the page's stored `currencySymbol`,
   * and the persist effect above skips null — so state and store agree.
   *
   * The five values come from `GRID_DISPLAY_DEFAULTS`, not from literals here,
   * and NOT from `initialToggles`: see that module for why the divergence from a
   * saved view's seed is deliberate and must stay row-REVEALING.
   *
   * ~~NOT reset here: ... and `pageDisplayExtras` (a page's own controls, e.g.
   * Aged A/R's practice area — that state belongs to the page).~~ SUPERSEDED
   * 2026-08-28. The reasoning is kept because half of it still holds — the state
   * does belong to the page and this grid still cannot reach it — but the
   * CONCLUSION was wrong, and it was wrong in the money direction.
   *
   * Two things that sentence got wrong. First, it described the slot as "a
   * page's own controls, e.g. Aged A/R's practice area", which reads as one
   * harmless select. Aged A/R is the ONLY page that supplies the slot, and what
   * it puts there is a practice-area select AND TWO ROW-HIDING CHECKBOXES —
   * "Hide zero balances" (which DEFAULTS ON, ARReport.jsx `useState(true)`) and
   * "Hide inactive/closed matters". Because AR also passes
   * `showZeroToggle={false} showInactiveToggle={false}`, those two are the ONLY
   * row-hiding controls anywhere in that menu: the grid's own hideZero /
   * hideInactive are suppressed from the UI, so resetting them reset a SHADOW
   * COPY nothing on screen was bound to.
   *
   * Second, "belongs to the page" was treated as a reason to skip it rather than
   * as a reason to ASK the page. So on report 21 — the named specialized
   * baseline — a user ticked a row-hiding box, clicked "Reset grid layout" two
   * rows below it, watched something happen, and kept hiding A/R rows.
   *
   * The seam: the page hands down `onResetPageDisplayExtras` alongside
   * `pageDisplayExtras`, exactly as this component hands
   * `onResetDisplayOptions` down to DataGrid. A page that supplies the controls
   * and NOT the hook is not silently ignored any more — `displayToggles` below
   * renders a visible caveat in the menu saying so (see `pageExtrasUnresettable`).
   *
   * Still NOT reset: `recompute` ("Total just the filtered rows"), which lives
   * on the totals strip below the grid rather than in this menu, and defaults ON.
   */
  /*
   * Spec 171 — the per-column filter's operator (Contains / Starts with / Ends
   * with). DataGrid draws the filter row and hands ColumnValuePicker four fixed
   * props, and DataGrid is the grid for 12 non-report callers too, so the
   * opt-in travels by CONTEXT around it (columnFilterMatchModeContext.js).
   * `resetKey` moves when "Reset grid layout" runs — DataGrid clears the column
   * filters themselves and then calls handleResetDisplayOptions below — so an
   * operator chosen before the reset goes back to Contains with them.
   */
  const [filterModeResetKey, setFilterModeResetKey] = useState(0)
  const matchModeContext = useMemo(
    () => ({ enabled: true, resetKey: filterModeResetKey }),
    [filterModeResetKey]
  )

  const handleResetDisplayOptions = useCallback(() => {
    setForceCurrency(GRID_DISPLAY_DEFAULTS.forceCurrency)
    // Spec 212: DataGrid's resetPagePreferences has already deleted the stored flag.
    setShowThousands(GRID_DISPLAY_DEFAULTS.showThousands)
    setHideZero(GRID_DISPLAY_DEFAULTS.hideZero)
    setHideInactive(GRID_DISPLAY_DEFAULTS.hideInactive)
    setAgingMode(GRID_DISPLAY_DEFAULTS.agingMode)
    setCustomDays(GRID_DISPLAY_DEFAULTS.customDays)
    // Spec 171: the column filters' operators, alongside the filters DataGrid
    // has just cleared.
    setFilterModeResetKey((k) => k + 1)
    // LAST, and via the page, because this grid does not own that state.
    onResetPageDisplayExtras?.()
  }, [onResetPageDisplayExtras])

  // Apply the toggles to the rows the grid renders. Totals stay coherent for
  // free: isFiltered compares the visible rows to the FULL data.length, so a
  // toggle reads as "filtered" and the CC-49456 recompute reflects what's shown.
  const displayData = useMemo(() => {
    let rows = data
    if (hideZero && numericColIds.length > 0) {
      rows = rows.filter(
        (r) => !isHideableZeroRow(numericColIds, amountColIds, (id) => toNumber(r?.[id]?.sortVal ?? r?.[id]?.display))
      )
    }
    if (hideInactive && statusColId) {
      rows = rows.filter((r) => !isInactiveStatusValue(r?.[statusColId]?.display ?? ''))
    }
    if (agingActive && agingColId) {
      rows = rows.filter((r) => isStaleValue(r?.[agingColId]?.display ?? '', staleDays))
    }
    return rows
  }, [data, hideZero, hideInactive, numericColIds, amountColIds, statusColId, agingActive, agingColId, staleDays])

  // Report-level totals. CB's reportFooterCells are server values over the FULL
  // dataset. Build a per-column footer model + classify which columns are safe
  // to recompute as a SUM when rows are filtered (reportGridUtils.summableColumns:
  // only columns whose server total already equals the sum of every row).
  const { footerCols, footerNumericIds, summable } = useMemo(() => {
    const byId = new Map((report?.reportFooterCells || []).map((c) => [String(c.columnId), c.displayValue]))
    const serverTotals = new Map()
    const allValues = new Map()
    for (const [colId, disp] of byId.entries()) {
      const n = toNumber(disp)
      if (n != null) serverTotals.set(colId, n)
    }
    for (const colId of serverTotals.keys()) {
      const vals = []
      for (const row of data) {
        const n = toNumber(row?.[colId]?.sortVal ?? row?.[colId]?.display)
        if (n != null) vals.push(n)
      }
      allValues.set(colId, vals)
    }
    const cols = cbColumns
      .map((col) => {
        const colId = String(col.columnId)
        const serverDisplay = byId.get(colId)
        if (serverDisplay == null || String(serverDisplay).trim() === '') return null
        // Spec 133: a parseable footer never makes a column CB declares
        // TEXT / DATE / … numeric (footerMarksNumeric).
        return {
          colId,
          label: col.label,
          serverDisplay,
          // Spec 211: the totals strip groups digits exactly as its column's cells do.
          group: (html) => groupedDisplay(col, html, numericTotals, showThousands),
          isNumeric: footerMarksNumeric(col, serverTotals.has(colId)),
          // CC-53905 review fix 4: Show $ reads the shared numeric test here too.
          showDollar: showDollarNumeric(col, numericTotals),
          currency: currencyEligible(col, currencyMarked),
        }
      })
      .filter(Boolean)
    // Declared footer aggregates (CC-49302) make the SUM-only guardrail authoritative.
    const aggregateByCol = new Map(cbColumns.map((c) => [String(c.columnId), columnAggregate(c)]))
    return {
      footerCols: cols,
      footerNumericIds: new Set(cols.filter((c) => c.isNumeric).map((c) => c.colId)),
      summable: summableColumns(allValues, serverTotals, aggregateByCol),
    }
  }, [cbColumns, report, data, currencyMarked, numericTotals, showThousands])

  // Visible (filtered) rows + ordered visible column ids surfaced by DataGrid —
  // drives the totals badge/recompute and the export-what-you-see view (CC-49370).
  const [visibleRows, setVisibleRows] = useState(null)
  const [visibleColumnIds, setVisibleColumnIds] = useState(null)
  // CC-50311: the `data` array `visibleRows` was derived from. DataGrid publishes
  // the filtered rows from an EFFECT, which commits one render after `displayData`
  // changes — so between those two commits `visibleRows` describes the PREVIOUS
  // row set while `data.length` is already the new one. Without this we read that
  // mismatch as "the user filtered" and re-sum a stale row set into the totals
  // strip; on a fresh run the stale set is empty, which is how Trial Balance
  // rendered $0.00 debits and credits over two real rows. Keeping the source
  // array lets us prove a publication is CURRENT before trusting it for money.
  const [visibleRowsSource, setVisibleRowsSource] = useState(null)
  // CC-49456: recompute defaults ON, so filtering re-totals the visible rows by
  // default (the usual intent when filtering); user can uncheck to see the full
  // unfiltered totals. SUM-only guardrail unchanged.
  const [recompute, setRecompute] = useState(initialToggles?.recompute ?? true)
  // Current quick-filters (CC-49732) — held in a ref (only read at save time).
  const columnFiltersRef = useRef(initialPageState?.columnFilters || [])
  const onVisibleRowsChange = useCallback((rows, ids, colFilters, sourceData) => {
    setVisibleRows(rows)
    setVisibleRowsSource(sourceData ?? null)
    if (ids) setVisibleColumnIds(ids)
    if (colFilters) columnFiltersRef.current = colFilters
  }, [])

  // CC-50311: only a publication derived from the rows currently on screen may
  // drive a recompute. When it is stale (or a caller didn't tell us its source)
  // we deliberately fall through to CB's server total — the authoritative figure,
  // and already what the strip shows when the user unchecks the recompute box.
  // Surfacing the server number always beats re-summing rows we can't vouch for.
  const rowsAreCurrent = visibleRowsSource != null && visibleRowsSource === displayData
  const isFiltered = rowsAreCurrent && visibleRows != null && visibleRows.length !== data.length

  // CC-49354 #3: surface the filtered state so ReportView can warn on the export
  // control when the grid is filtered but the export scope boxes are unchecked.
  useEffect(() => {
    onFilteredChange?.(isFiltered)
  }, [isFiltered, onFilteredChange])

  // Publish the current view for the export control (CC-49370): every column with
  // its visibility + order, and every row (visible = passes the active filters),
  // values as the plain text the user sees.
  useEffect(() => {
    if (!exportViewRef) return
    exportViewRef.current = () => {
      const ids = visibleColumnIds
      // CC-49872: a numeric SERVER total marks its column numeric even when
      // CB's metadata hides it (Payment/Credit Allocation's Amount is
      // CENTER-aligned with filterType NONE) — otherwise the export writes the
      // amounts as text and the totals row can't sum them. The server total
      // itself rides along per column as the totals-row fallback.
      // Spec 133: the fallback never overrides a type CB DECLARES non-numeric.
      // CC-53905 review fix 4: this IS `showDollarNumeric` — the cells, totals
      // and toggle above use the same test, so Show $ acts on the same columns
      // on screen and in the file.
      const colIsNumeric = (col) => showDollarNumeric(col, numericTotals)
      const serverTotalById = new Map(footerCols.map((f) => [f.colId, exportText(String(f.serverDisplay ?? ''))]))
      const columns = cbColumns.map((col, idx) => {
        const id = String(col.columnId)
        const order = ids ? ids.indexOf(id) : idx
        // `summable` = safe to total (CC-49457): the SUM-only guardrail (CC-49382)
        // so the export's totals row never re-adds an average/rate/weighted column.
        // CC-49337: sample-derived display format so the Excel export can
        // apply a real number format (currency symbol, separators, decimals).
        const numeric = colIsNumeric(col)
        const sample = numeric ? data.find((r) => r?.[id]?.display)?.[id]?.display : null
        const fmt = numeric ? detectNumberFormat(sample) : null
        // CC-53905: Show $ grants currency only where the screen shows a "$"
        // (currencyEligible), and keeps the display's own decimals — a C0 column
        // exports "$1,541,996", never a forced "$1,541,996.00".
        const forced = forceCurrency === true && numeric && !fmt?.percent && currencyEligible(col, currencyMarked)
        // Spec 211: grouped on screen, text in this view — the PDF groups it too.
        const thousands = !numeric && groupsThousands(col, numericTotals)
        // Spec 212: with "Show commas in numbers" off, the PDF and the client
        // XLSX / CSV formats print every figure without separators, as the screen.
        return { key: id, label: col.label, visible: ids ? ids.includes(id) : true, order: order === -1 ? 999 + idx : order, numeric, summable: summable.has(id), serverTotal: serverTotalById.get(id) ?? '', currency: forceCurrency === false ? false : ((fmt?.currency ?? false) || forced), decimals: fmt?.decimals ?? 0, ...(thousands ? { thousands } : {}), ...(showThousands ? {} : { grouping: false }) }
      })
      // Numeric columns export as REAL numbers (CC-49370 fix) so totals stay
      // summable in Excel; text columns export the plain text (spec 133:
      // `exportCell`, one converter for every view builder).
      const toValues = (row) => Object.fromEntries(cbColumns.map((col) => {
        const id = String(col.columnId)
        return [id, exportCell(row[id]?.display ?? '', row[id]?.sortVal, colIsNumeric(col))]
      }))
      // CC-49860: publish the FULL footer block so exports print every line CB
      // sent, not just the first. Most flat reports have a single footer row, in
      // which case this is exactly reportFooterCells; the multi-row shape shows up
      // when a CSV-backed report closes with e.g. "Total / PTD" + "Total / YTD".
      // Values are verbatim server figures — only row 0 is validated against the
      // SUM-only guardrail, so rows 1..N must never be recomputed.
      // A footer cell's number is read from its display (no dataValue here).
      const footerCellsToValues = (cells) => Object.fromEntries(cbColumns.map((col) => {
        const id = String(col.columnId)
        const cell = (cells || []).find((c) => String(c.columnId) === id)
        return [id, exportCell(cell?.displayValue ?? '', undefined, colIsNumeric(col))]
      }))
      const rawFooterRows = report?.reportFooterRows?.length
        ? report.reportFooterRows
        : (report?.reportFooterCells?.length ? [report.reportFooterCells] : [])
      return {
        columns,
        // visibleRows = filtered + sorted (display order); allRows = base order.
        // CC-50311: a stale publication must not become an "export what you see"
        // row set either — fall back to the full data rather than write rows that
        // no longer match the screen.
        visibleRows: ((rowsAreCurrent && visibleRows) || data).map(toValues),
        allRows: data.map(toValues),
        footerRows: rawFooterRows.map(footerCellsToValues),
        // CC-50004: the tri-state "Show $ on amounts" choice, so the export can
        // honor it. null = auto (mirror the data, no explicit choice); true/false =
        // the user said so, and the file has to agree with the screen.
        // CC-53905: only where the toggle is offered (`currencyActs`) — a choice
        // stored on a report it cannot act on is no choice for the export either.
        currencyOverride: currencyActs ? (forceCurrency ?? null) : null,
      }
    }
  })

  // Publish the current page state for the Views menu (CC-49732): the grid
  // transformations + page toggles a saved view should capture. Same lifecycle
  // as exportViewRef — refreshed every render so it's never stale.
  useEffect(() => {
    if (!pageStateRef) return
    pageStateRef.current = () => ({
      columnFilters: columnFiltersRef.current || [],
      toggles: { forceCurrency, showThousands, hideZero, hideInactive, agingMode, customDays, recompute },
    })
  })

  // Value to show for each footer column given the current filter/toggle state.
  const footerValue = (colId, serverDisplay) => {
    if (!isFiltered || !recompute) return { html: serverDisplay }
    if (!summable.has(colId)) {
      // CC-49354 #4: a TEXT label cell carrying a row count - AutoReportGrid's
      // "Total (140)" - must follow the filter too, or the strip contradicts
      // the rows on screen. Only non-numeric "(<digits>)" cells are rewritten;
      // numeric non-summable cells keep the server value (the "i" marker case).
      const text = String(serverDisplay ?? '')
      if (!footerNumericIds.has(colId) && /\(\s*\d[\d,]*\s*\)/.test(text)) {
        return { html: text.replace(/\(\s*\d[\d,]*\s*\)/, `(${(visibleRows || []).length})`), recomputed: true }
      }
      return { html: serverDisplay }
    }
    const sum = (visibleRows || []).reduce((acc, row) => {
      const n = toNumber(row?.[colId]?.sortVal ?? row?.[colId]?.display)
      return acc + (n ?? 0)
    }, 0)
    return { html: formatLikeSample(serverDisplay)(sum), recomputed: true }
  }

  // CC-49735: totals render inside DataGrid's tfoot, one cell per column, so
  // each figure sits directly under its column (order/visibility/width/alignment
  // all honored). The ✓ / “i” markers ride along in the cell.
  const footerCellNodes = {}
  for (const f of footerCols) {
    const v = footerValue(f.colId, f.serverDisplay)
    const grouped = f.group(v.html)
    // CC-49743: the totals row follows the Show-$ toggle like the data cells.
    // CC-53905: the "$" goes onto the total CB displayed (already rounded to the
    // column's declared decimals) — never a re-parse padded to 2 decimals.
    const footerHtml = !f.showDollar
      ? grouped
      : forceCurrency === true
        ? (f.currency ? withCurrencySymbol(grouped) : grouped)
        : forceCurrency === false
          ? stripCurrencySymbol(grouped)
          : grouped
    footerCellNodes[f.colId] = (
      <span className="inline-flex flex-wrap items-center gap-1">
        {/* eslint-disable-next-line react/no-danger */}
        <span dangerouslySetInnerHTML={{ __html: footerHtml }} />
        {isFiltered && recompute && summable.has(f.colId) && (
          <span className="text-[10px] text-gray-500" title="Recomputed from the rows currently shown">✓</span>
        )}
        {isFiltered && recompute && f.isNumeric && !summable.has(f.colId) && (
          <span
            role="img"
            aria-label={`Server value: “${f.label}” isn’t a simple sum (it’s an average, rate, or weighted figure), so re-adding the visible rows would be incorrect. This total stays at the server value for the full dataset.`}
            title={`“${f.label}” isn’t a simple sum (e.g. an average, rate, or weighted value), so re-adding only the visible rows would give a wrong number. To stay accurate, this total keeps the server-calculated value for the full dataset.`}
            className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border border-gray-400 text-[9px] leading-none text-gray-500 cursor-help"
          >
            i
          </span>
        )}
      </span>
    )
  }

  /*
   * spec 092 — these were a naked row of checkboxes floating above the grid: the
   * scruffiest part of the old report page, and unlabelled as a group. They are
   * the same controls, handed to the grid's Display View panel, which is where
   * the mock puts "everything that only changes how the table looks".
   * Still gated on being able to act on this report's data.
   *
   * A2 (2026-08-26) — `text-xs text-gray-600` deleted from the wrapper below.
   * DataGrid renders this node inside its View-options block, which already sets
   * `text-[13px] text-rc-ink-2` (DataGrid.jsx: the `displayViewExtras` wrapper);
   * these two utilities were OVERRIDING it, so the very same three checkboxes
   * came out 12px/#4b5563 on the 33 flat reports and 13px/#374151 on the 86
   * grouped ones. Nobody chose that — it is the inherited-vs-overridden accident
   * you get from styling a slot's content and its host separately. Inheriting is
   * the fix; do not re-add a size or colour here, put it on the panel.
   *
   * B4 + B5 (Himaad/Jordan, spec 117) — ONE defect, not two. Inside this single
   * popover the COLUMN rows above were a drawn 14px box on a `hover:bg-gray-50`
   * row (DataGrid.jsx:1146) and these were native checkboxes with no row hover,
   * which is exactly what was described: "the view options checkbox below looks
   * lighter color" and "this one doesn't have the hover". So these adopt the
   * column rows' control (`ControlCheckbox`) and their row treatment.
   *
   * `-mx-3.5 px-3.5` rather than moving the padding: DataGrid.jsx:1222 owns the
   * `px-3.5` wrapper around this slot and is another stream's file this batch, so
   * each row cancels that inset for its own hover band and restores it for its
   * content. It also leaves `pageDisplayExtras` — a PAGE's contributed nodes,
   * rendered as siblings below — untouched, which is deliberate: those live in
   * files outside this change (ARReport.jsx) and must not be half-restyled from
   * here. See spec 117 for that gap, which is real and reported.
   *
   * The `gap-2` between rows is kept on purpose. Going flush would match the
   * column list pixel-for-pixel but would also re-space Aged A/R's contributed
   * rows, and neither Himaad nor Jordan named row PITCH — they named the box and
   * the hover.
   */
  /* `has-[:focus-visible]` and NOT `focus-within`, corrected 2026-09-08.
     `:focus-within` matches after a MOUSE click too — activating a <label>
     focuses its control — and these rows do not dismiss the popover, so the
     band stayed painted on a clicked row after the pointer left. Hover a column
     row next and two rows read as hovered at once, which the column rows never
     do: a new instance of the very "these two rows behave differently" defect
     B5 exists to remove. `:focus-visible` is not matched on a checkbox clicked
     with a pointer, so the band is now keyboard-only.

     WHAT THE COLUMN ROWS ACTUALLY CARRY — corrected 2026-09-09, twice wrong
     before this. The first version of this note claimed parity with a
     `focus-visible:bg-gray-50` on the column rows; the second "corrected" it to
     "the column rows carry NO background focus treatment at all", which is
     equally false. The two grids DISAGREE, and both halves of the popover are
     involved:
       - DataGrid.jsx:1161 (this grid's column button) — `focus-visible:none
         focus-visible:underline`, no band. So on the 33 flat reports the line
         below is an ADDITION.
       - ReportGroupedGrid.jsx:1499 (the grouped grid's column button) —
         `hover:bg-gray-50 focus-visible:bg-gray-50`. So on the 86 grouped
         reports it is PARITY.
       - DataGrid.jsx:1236/:1252 and ReportGroupedGrid.jsx:1534/:1591 (Freeze
         first column / Reset layout, in the SAME popover on both grids) — all
         four carry the band.
     i.e. three of the four row kinds in this popover already have it and only
     the flat grid's column button does not: a CC-50013-shaped divergence this
     sweep walked past, recorded in spec 117 §5. Do not "simplify" the band away
     on the strength of a claim about one grid — the grouped assertion is pinned
     in controlSweep.spec117.test.jsx so this cannot rot again.

     And the band is NOT the focus indicator. #f9fafb on the white panel is
     1.024:1, imperceptible. What indicates focus is the box's own 2px
     --color-rc-chip-text outline (ControlCheckbox) — the band is row-level
     parity with `hover`, nothing more.

     ⚠️ MOVED 2026-09-09 (spec 121 gap 2): the string itself now lives beside the
     control it pairs with, exported from ControlCheckbox.jsx, because a THIRD site
     needs it — ARReport.jsx's two `pageDisplayExtras` toggles render as siblings of
     these rows and had `gap-1.5` with no band, which is half of B4/B5 reproduced by
     a file partition. Everything above still describes it. */
  const displayToggles = (currencyActs || thousandsActs || (numericColIds.length > 0 && showZeroToggle) || (hasInactiveRows && showInactiveToggle) || hasAgingDates || pageDisplayExtras) ? (
        <div data-print-hide className="flex flex-col gap-2">
          {currencyActs && (
            <label className={TOGGLE_ROW}>
              <ControlCheckbox
                checked={effectiveCurrency}
                data-pendo="report-currency-toggle"
                onChange={(e) => {
                  trackGridTransform(storageKey, 'currency_toggle', e.target.checked ? 'on' : 'off')
                  setForceCurrency(e.target.checked)
                }}
              />
              Show $ on amounts
            </label>
          )}
          {thousandsActs && (
            <label className={TOGGLE_ROW}>
              <ControlCheckbox
                checked={showThousands}
                data-pendo="report-thousands-toggle"
                onChange={(e) => {
                  trackGridTransform(storageKey, 'thousands_toggle', e.target.checked ? 'on' : 'off')
                  setShowThousands(e.target.checked)
                  if (storageKey) prefsStore.setPageFlag?.(storageKey, THOUSANDS_PAGE_FLAG, e.target.checked)
                }}
              />
              Show commas in numbers
            </label>
          )}
          {numericColIds.length > 0 && showZeroToggle && (
            <label className={TOGGLE_ROW}>
              <ControlCheckbox
                checked={hideZero}
                onChange={(e) => setHideZero(e.target.checked)}
              />
              Hide $0 / zero-balance rows
            </label>
          )}
          {hasInactiveRows && showInactiveToggle && (
            <label className={TOGGLE_ROW}>
              <ControlCheckbox
                checked={hideInactive}
                onChange={(e) => setHideInactive(e.target.checked)}
              />
              Hide inactive / closed
            </label>
          )}
          {hasAgingDates && (
            <span
              className="inline-flex items-center gap-1.5"
              title={`Show only rows whose “${agingCol.label}” is more than the chosen number of days ago. Rows with no date count as never — they stay shown.`}
            >
              <span>No activity in</span>
              <select
                aria-label={`Inactivity window (${agingCol.label})`}
                value={agingMode}
                onChange={(e) => {
                  setAgingMode(e.target.value)
                  if (e.target.value !== 'custom') setCustomDays('')
                }}
                className="border border-gray-200 rounded px-1.5 py-0.5 text-xs focus:ring-2 focus:ring-cb-accent/20 focus:border-cb-accent"
              >
                <option value="">Off</option>
                {AGING_PRESET_DAYS.map((d) => (
                  <option key={d} value={String(d)}>{d}+ days</option>
                ))}
                <option value="custom">Custom…</option>
              </select>
              {agingMode === 'custom' && (
                <span className="inline-flex items-center gap-1">
                  <input
                    type="number"
                    min="1"
                    value={customDays}
                    aria-label="Custom inactivity days"
                    placeholder="days"
                    onChange={(e) => setCustomDays(e.target.value)}
                    className="w-16 border border-gray-200 rounded px-1.5 py-0.5 text-xs focus:ring-2 focus:ring-cb-accent/20 focus:border-cb-accent"
                  />
                  <span>days</span>
                </span>
              )}
              <span className="text-gray-400">({agingCol.label})</span>
            </span>
          )}
          {pageDisplayExtras}
          {/*
            A page that contributes controls the reset cannot reach says so, out
            loud, in the menu — see PAGE_EXTRAS_NOT_RESET_NOTE. Rendered ONLY in
            that configuration, so it is invisible on Aged A/R (which now passes
            the hook) and on every page that contributes nothing.

            The size/colour ARE set here, deliberately departing from the "let it
            inherit the panel" rule that governs the toggle labels above: this is
            a caveat, not an option, and it has to read as one. Same pair as
            ReportExport.jsx's own panel caveat. --color-rc-stale-text #92400e on
            the panel's bg-white computes to 7.09:1 — AA at 11px with room, and
            6.78:1 even against a gray-50 hover, though nothing here hovers.
          */}
          {pageDisplayExtras && !onResetPageDisplayExtras && (
            <span data-testid="page-extras-not-reset" className="text-[11px] text-rc-stale-text">
              {PAGE_EXTRAS_NOT_RESET_NOTE}
            </span>
          )}
        </div>
  ) : null

  return (
    <div className="space-y-2">
      <ColumnFilterMatchModeContext.Provider value={matchModeContext}>
      <DataGrid
        headerRow={headerRow}
        columns={columnsWithPickers}
        data={displayData}
        storageKey={storageKey}
        enableExport={false}   /* ReportView already offers server-side export */
        enableFiltering={true}
        /* spec 092 — the mock's result strip: row count + note + Display View +
           Column Filter. Prop-gated so the nine non-report pages that share this
           grid keep the toolbar they have today. */
        reportChrome={true}
        gridNote={gridNote}
        displayViewExtras={displayToggles}
        onResetDisplayOptions={handleResetDisplayOptions}
        stripActions={stripActions}
        enableLayout={true}    /* CC-49380 resize + Reset, scoped to report grids */
        stickyHeader={true}    /* frozen header on a fixed-height scroll region */
        enablePinning={true}   /* CC-49462 frozen identity column on wide reports */
        normalCaseHeaders={true} /* readable sentence-case headers, not ALL-CAPS */
        emptyMessage="No data for the selected filters"
        defaultSorting={defaultSorting}
        onVisibleRowsChange={onVisibleRowsChange}
        initialColumnFilters={initialPageState?.columnFilters}
        enableAlignment={true}
        columnAlignment={alignOverrides}
        onColumnAlignmentChange={handleAlignmentChange}
        {...(footerCols.length > 0 ? { footerCells: footerCellNodes } : {})}
        {...(rowNoun ? { rowNoun } : {})}
        /* CC-50173: the report's own name is the most useful accessible name for
           this table; DataGrid falls back to a generic one when it is absent. */
        {...(report?.name ? { tableLabel: `${report.name} results` } : {})}
      />
      </ColumnFilterMatchModeContext.Provider>
      {/* Filtered-totals coherence bar (CC-49382/CC-49456) — the totals live in
          the grid's footer row (CC-49735); this bar only explains scope while a
          filter is active and hosts the recompute opt-out. */}
      {footerCols.length > 0 && isFiltered && (
        <div className="bg-gray-100 rounded-lg px-4 py-2 flex items-center gap-3">
          <span className={`text-xs ${recompute ? 'text-gray-600' : 'text-amber-700'}`}>
            {recompute
              ? `Totals reflect the ${formatCount(visibleRows?.length ?? 0)} filtered row${(visibleRows?.length ?? 0) === 1 ? '' : 's'} shown — uncheck to see the full unfiltered totals (all ${formatCount(data.length)}). Columns marked “i” aren’t simple sums (averages, rates) and keep their server value.`
              : `Totals reflect the full dataset — all ${formatCount(data.length)} rows, not your current filter. Check the box to total just the ${formatCount(visibleRows?.length ?? 0)} filtered rows.`}
          </span>
          {/* B4 (spec 117): the drawn control. No row hover here — this is a bar,
              not a list of rows, and the hover band would have nothing to be a
              band OF. gap-1.5 -> gap-2.5 to match every other converted site. */}
          <label className="ml-auto inline-flex items-center gap-2.5 text-xs text-gray-600 cursor-pointer whitespace-nowrap">
            <ControlCheckbox
              checked={recompute}
              onChange={(e) => setRecompute(e.target.checked)}
            />
            Total just the filtered rows
          </label>
        </div>
      )}
    </div>
  )
}

export default ReportDataGrid
